import React, { useMemo } from 'react';
import type { ComponentProps } from 'react';

import { Menu } from '@base-ui/react/menu';

import type { DeclarativeMenuConfig } from './build-declarative-menu';
import { buildDeclarativeMenu } from './build-declarative-menu';

type TriggerProps = ComponentProps<typeof Menu.Trigger>;

export interface DeclarativeMenuProps extends Omit<TriggerProps, 'children' | 'render'> {
  children: TriggerProps['render'];
  config: DeclarativeMenuConfig;
  zIndex?: number;
}

/**
 * A menu system based on Base UI's Menu components where all menu items and submenus are specified
 * using a declarative config object.
 *
 * Base UI's Menu component has several advantages over MUI's Menu component, including better UX
 * and compatibility with Base UI's Menubar component. For this reason, consider migrating existing
 * MUI menus to use this component.
 *
 * @example
 *   const menu = useMemo((): DeclarativeMenuConfig => [
 *     { icon: <SomeIcon />, label: 'A menu item', onClick: someHandler },
 *     DECLARATIVE_MENU_SEPARATOR,
 *     {
 *       label: 'Submenu',
 *       submenu: [
 *         { label: 'A menu item in a submenu' },
 *       ],
 *     },
 *   ], [someHandler]);
 *
 *   <DeclarativeMenu config={menu}>
 *     {(renderProps, state) => (
 *       <SelectButton
 *         disabled={state.disabled}
 *         selected={state.open}
 *         style={{ fontWeight: 'normal' }}
 *         {...renderProps}
 *       >
 *         Menu trigger
 *       </SelectButton>
 *     )}
 *   </DeclarativeMenu>
 *
 * @see buildDeclarativeMenu
 */
export function DeclarativeMenu(props: DeclarativeMenuProps) {
  const { children: renderTrigger, config, zIndex, ...other } = props;
  const popup = useMemo(() => buildDeclarativeMenu(config, { zIndex }), [config, zIndex]);

  return (
    <Menu.Root>
      <Menu.Trigger render={renderTrigger} {...other} />
      {popup}
    </Menu.Root>
  );
}
